import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { useTrialStatus } from './useTrialStatus';

export interface WritePermission {
  /** False when the UI must not offer create/edit/delete actions. */
  canWrite: boolean;
  /** The user is a read-only member of this organization (REVIEWER-style). */
  isReadOnly: boolean;
  /** The organization's trial lapsed and it restricts this user — see useTrialStatus. */
  isBillingLocked: boolean;
}

/**
 * One question — "may this user write?" — with two independent causes behind it.
 *
 * They are kept apart deliberately: `isReadOnly` is about *this person's* membership,
 * `isBillingLocked` is about *the organization's* subscription, and the message shown to
 * the user differs ("your access is read-only" vs "the trial ended, add a card"). Merging
 * them into one flag would make it impossible to say the right thing.
 *
 * Components should check `canWrite` and let this hook own the combination, so a new cause
 * can be added later without touching every call site.
 *
 * Note this is presentation only. The server refuses the write regardless — see
 * SubscriptionWriteInterceptor. Disabling a button is a courtesy, not the enforcement.
 */
export const useCanWrite = (): WritePermission => {
  const isReadOnly = useAppSelector(
    (state: RootState) => state.user.isReadOnly ?? false,
  );
  const { isBillingLocked } = useTrialStatus();

  return {
    canWrite: !isReadOnly && !isBillingLocked,
    isReadOnly,
    isBillingLocked,
  };
};

export default useCanWrite;
